import React, { useId } from 'react';
import { rem } from 'polished';
import { H3 } from '../../primitives';
import ImageWithCaption from '../../components/Image/ImageWithCaption';
import styled from '@emotion/styled';
import { animations, mixins } from '../../core/styles/theme/default';
import { ImageInterface } from '~/types';

const StyledStory = styled.article`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  ${mixins.responsiveStyles({
    height: { zero: 'auto', medium: '100%' },
    'min-height': { zero: 300, medium: 420 },
  })}
`;

const StyledStoryMain = styled.a`
  flex: 1;
  position: relative;
  z-index: 1;
  padding: ${rem(32)};
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  text-decoration: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 20%,
    rgba(0, 0, 0, 0.75) 75%
  );

  &:hover {
    .circle {
      fill: orange;
    }
  }

  .m-richtext & {
    text-decoration: none;
  }
`;

const StyledBackground = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  overflow: hidden;

  picture,
  figure,
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }

  .gatsby-image-wrapper {
    backface-visibility: hidden;
  }

  picture {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 1;
    transform: scale(1.07);
    transition: opacity ${animations.default}, transform ${animations.default};
  }

  .item:hover & {
    picture {
      opacity: 0.8;
      transform: scale(1);
    }
  }
`;

const StyledLogo = styled.div`
  display: flex;
  align-items: center;
  color: white;
  font-weight: 100;
  font-size: ${rem(16)};
  line-height: ${rem(20)};
  letter-spacing: ${rem(0.5)};
  margin-bottom: ${rem(8)};

  svg {
    margin-right: ${rem(2.5)};
  }

  b {
    font-weight: 500;
  }
`;

const StyledTitle = styled(H3)`
  color: white;
  padding-right: ${rem(32)};
  overflow-wrap: break-word;

  ${({ theme }) => theme?.stylesExtension?.teaserStorys?.title}
`;

const TeaserStorys = (props: TeaserStorysProps) => {
  const { title, href, teaserImage, logoText, belongsToCampaign, trackClick } =
    props.data;
  const iconUuid = useId();

  return (
    <>
      <StyledStory
        className={'item'}
        onClick={() => {
          trackClick?.();
        }}
      >
        {teaserImage ? (
          <StyledBackground>
            <ImageWithCaption data={{ ...teaserImage, disableLink: true }} />
          </StyledBackground>
        ) : null}
        <StyledStoryMain href={href}>
          {!belongsToCampaign && (
            <StyledLogo>
              <svg
                fill="none"
                viewBox="0 0 23 22"
                width="24"
                height="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id={`${iconUuid}_a`}
                    x1="18.984"
                    x2="3.597"
                    y1="18.831"
                    y2="3.5042"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#fff" offset="0" />
                    <stop stopColor="#fff" stopOpacity=".6" offset="1" />
                  </linearGradient>
                </defs>
                <path
                  d="m10.836 22h0.8921v-3.8382h-0.8921v3.8382zm-4.0146-0.9596 0.8284 0.3199 1.4656-3.5824-0.8284-0.3198-1.4656 3.5823zm6.7547-3.2625 1.4657 3.5824 0.8284-0.3838-1.4657-3.5824-0.8284 0.3838zm-10.259 0.7037 0.63723 0.6397 2.7401-2.7507-0.63723-0.6397-2.7401 2.7507zm12.617-2.047 2.7401 2.7507 0.6372-0.6397-2.7401-2.7507-0.6372 0.6397zm-14.784-1.5353 0.38234 0.8316 3.5685-1.4713-0.38234-0.8317-3.5685 1.4714zm16.504-0.7037 3.5685 1.4713 0.3186-0.8316-3.5685-1.4713-0.3186 0.8316zm-17.142-2.6228h3.8234v-0.8956h-3.8234v0.8956zm17.779 0h3.8234v-0.8956h-3.8234v0.8956zm-17.205-4.1581 3.5685 1.4713 0.31862-0.83162-3.5685-1.4713-0.31862 0.83162zm16.504 0.57573 0.3823 0.83162 3.5685-1.4713-0.3823-0.83161-3.5685 1.4713zm-14.274-4.2221 2.7401 2.7507 0.63723-0.63971-2.7401-2.7507-0.63723 0.6397zm12.617 2.0471 0.6372 0.63971 2.7401-2.7507-0.6372-0.6397-2.7401 2.7507zm-9.2399-4.5419 1.4656 3.5824 0.8284-0.38382-1.4656-3.5824-0.82841 0.38382zm6.8821 3.1985 0.8284 0.31985 1.4657-3.5824-0.8284-0.31985-1.4657 3.5824zm-2.7401-0.38382h0.8921v-3.8382h-0.8921v3.8382z"
                  fill={`url(#${iconUuid}_a)`}
                  className="circle"
                />
              </svg>
              {logoText}
            </StyledLogo>
          )}
          <StyledTitle>{title}</StyledTitle>
        </StyledStoryMain>
      </StyledStory>
    </>
  );
};

export type TeaserStorysProps = {
  data: {
    title: string;
    categoryTitle?: string;
    teaser?: string;
    teaserImage: ImageInterface;
    slug?: string;
    node_locale: string;
    href: string;
    logoText?: string | React.ReactNode;
    linkText?: string;
    folder?: string;
    belongsToCampaign?:
      | {
          slug: string;
        }
      | boolean;
    trackClick?: () => void;
  };
};

export default TeaserStorys;
